import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAccessToken } from '@/features/auth/state/auth'
import api, { withAuthenticatedHeaders, withAuthenticatedRequest } from '@/lib/api'
import { legacyQueryOptions } from '@/lib/query-defaults'

// Hook para criar um novo contrato
export function useCreateContract() {
  const queryClient = useQueryClient()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async ({ createContractDto }: { createContractDto: any }) => {
      if (!token) throw new Error('Usuário não autenticado')
      const response = await api.post(`/contracts`, createContractDto, withAuthenticatedRequest(token))

      if (response.data) {
        return response.data
      } else {
        throw new Error('Erro ao criar contrato')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] })
    }
  })
}
// Hook para fazer upload de um documento de contrato
export function useUploadContractDocument() {
  const queryClient = useQueryClient()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async ({ id_tenant, typeContrato, file }: { id_tenant: string; typeContrato: string; file: File }) => {
      if (!token) throw new Error('Usuário não autenticado')

      const formData = new FormData()
      formData.append('file', file)

      const response = await api.post(`/contracts/doc/${typeContrato}/${id_tenant}`, formData, {
        headers: withAuthenticatedHeaders(token, {
          'Content-Type': 'multipart/form-data'
        })
      })

      if (response.data) {
        return response.data
      } else {
        throw new Error('Erro ao fazer upload do documento do contrato')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] })
    }
  })
}

// Hook para deletar um contrato por ID
export function useDeleteContract() {
  const queryClient = useQueryClient()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async (id: number) => {
      if (!token) throw new Error('Usuário não autenticado')

      const response = await api.post(`/contracts/delete/${id}`, null, withAuthenticatedRequest(token))

      if (response.data) {
        return response.data
      } else {
        throw new Error(`Erro ao deletar contrato com ID ${id}`)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] })
    }
  })
}

// Hook para buscar contratos pelo status IsAtivo
export function useFetchContractsByIsAtivo(isAtivo: boolean) {
  const token = useAccessToken()

  return useQuery({
    ...legacyQueryOptions,
    queryKey: ['contracts', 'isAtivo', isAtivo],
    queryFn: async () => {
      if (!token) throw new Error('Token ausente')

      const response = await api.get(`/contracts/ativo`, {
        ...withAuthenticatedRequest(token),
        params: { isAtivo }
      })

      if (response.data) {
        return response.data.data
      } else {
        throw new Error('Erro ao buscar contratos')
      }
    },
    enabled: Boolean(token)
  })
}
// Hook para buscar contratos pelo status IsAtivo
export function useFetchContractsByIsNotAtivo() {
  const token = useAccessToken()

  return useQuery({
    ...legacyQueryOptions,
    queryKey: ['contracts'],
    queryFn: async () => {
      if (!token) throw new Error('Token ausente')

      const response = await api.get(`/contracts/desativado`, withAuthenticatedRequest(token))

      if (response.data) {
        return response.data.data
      } else {
        throw new Error('Erro ao buscar contratos')
      }
    },
    enabled: Boolean(token)
  })
}

// Hook para buscar contratos por nome, tipo de contrato e status IsAtivo
export function useSearchContracts({
  name,
  typeContrato,
  isAtivo
}: {
  name?: string
  typeContrato?: string
  isAtivo?: boolean
}) {
  const token = useAccessToken()

  return useQuery({
    ...legacyQueryOptions,
    queryKey: ['contracts', 'search', { name, typeContrato, isAtivo }],
    queryFn: async () => {
      if (!token) throw new Error('Token ausente')

      const response = await api.get(`/contracts/buscar`, {
        ...withAuthenticatedRequest(token),
        params: { name, typeContrato, isAtivo }
      })

      if (response.data) {
        return response.data.data
      } else {
        throw new Error('Erro ao buscar contratos')
      }
    },
    enabled: Boolean(token)
  })
}
